import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Tab, Tabs, Row, Col, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';

const SizeChartModal = ({
  show,
  handleClose,
  categories,
  sizeOptions = [],
  sizeTypes = [],
  sizeChartData,
  setSizeChartData,
  loading,
}) => {
  const [activeCategoryTab, setActiveCategoryTab] = useState(0);
  const [activeSubCategoryTab, setActiveSubCategoryTab] = useState(0);
  const [unitPreferences, setUnitPreferences] = useState({});
  const [inputValues, setInputValues] = useState({});

  const cmToInch = (cm) => {
    if (!cm) return '';
    const inches = parseFloat(cm) / 2.54;
    return Number.isInteger(inches) ? inches.toString() : inches.toFixed(2);
  };

  const inchToCm = (inch) => {
    if (!inch) return '';
    const cm = parseFloat(inch) * 2.54;
    return Number.isInteger(cm) ? cm.toString() : cm.toFixed(2);
  };
  // Reset tabs to first category/subcategory when modal is opened
  useEffect(() => {
    if (show) {
      setInputValues({});
      console.log('SizeChartModal opened with size Chart Data', sizeChartData);
      setActiveCategoryTab(0);
      setActiveSubCategoryTab(0);
      // Initialize unit preferences for all subcategories
      const initialUnits = {};
      categories.forEach(category => {
        category.selectedSubCategory?.forEach(subCat => {
          initialUnits[subCat.value] = 'inch'; // Default to cm
        });
      });
      setUnitPreferences(initialUnits);
    }
  }, [show, categories]);

  // Get the active category
  const activeCategory = categories[activeCategoryTab];

  // Get subcategories for the active category
  const activeSubCategories = activeCategory?.selectedSubCategory?.map(subCat => ({
    ...subCat,
    categoryId: activeCategory.categoryId
  })) || [];

  // Get the active subcategory object
  const activeSubCat = activeSubCategories[activeSubCategoryTab];

  // Filter sizes/types for the active subcategory
  const filteredSizes = sizeOptions.filter(s => s.subsubcategoryId === activeSubCat?.value);
  const filteredTypes = sizeTypes.filter(t => t.subsubcategoryId === activeSubCat?.value);

  const handleSizeChartChange = (categoryId, subCategoryValue, size, type, value) => {
    setSizeChartData(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [subCategoryValue]: {
          ...(prev[categoryId]?.[subCategoryValue] || {}),
          [size]: {
            ...(prev[categoryId]?.[subCategoryValue]?.[size] || {}),
            [type]: value
          }
        }
      }
    }));
  };

  const isValidNumberInput = (value) => {
    // Allow empty string (for clearing the field)
    if (value === '') return true;
    // Check if it's a valid number (including decimals)
    return /^[0-9]*\.?[0-9]*$/.test(value);
  };

  const handleUnitChange = (subCategoryValue, newUnit) => {
    setUnitPreferences(prev => ({
      ...prev,
      [subCategoryValue]: newUnit
    }));
    setInputValues({}); // Reset all input values to force re-calc from cm data
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      dialogClassName="modal-dialog-centered"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title className="w-100 text-center">Size Chart</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{
        height: '70vh',
        overflowY: 'auto',
        scrollbarColor: '#ced4da transparent',
        scrollbarWidth: 'thin',
        borderBottom: '1px solid #dee2e6',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {categories.length > 0 && (
          <>
            {/* Category Tabs */}
            <Row className="mb-1">
              <Col className="col-8">
                <Tabs
                  activeKey={activeCategoryTab}
                  onSelect={(k) => {
                    setActiveCategoryTab(Number(k));
                    setActiveSubCategoryTab(0);
                  }}
                  className="mb-1"
                >
                  {categories.map((category, catIndex) => (
                    <Tab
                      key={catIndex}
                      eventKey={catIndex}
                      title={
                        <span style={{
                          color: activeCategoryTab === catIndex ? 'black' : 'grey',
                          fontWeight: activeCategoryTab === catIndex ? 'bold' : 'normal'
                        }}>
                          {category.selectedCategory?.label || `Category ${catIndex + 1}`}
                        </span>
                      }
                    />
                  ))}
                </Tabs>
              </Col>
              {/* Unit Toggle for current subcategory */}
              <Col className="col-4 d-flex justify-content-end">
                {activeSubCat && (
                  <Row className="mb-3">
                    <Col>
                      <div className="d-flex align-items-center">
                        <span className="me-2 fs-4">Unit :</span>
                        <ToggleButtonGroup
                          type="radio"
                          name="unit-toggle"
                          value={unitPreferences[activeSubCat.value] || 'cm'}
                          onChange={(val) => handleUnitChange(activeSubCat.value, val)}
                        >
                          <ToggleButton
                            id={`tbg-btn-2-${activeSubCat.value}`}
                            value="inch"
                            variant="outline-secondary"
                            size="sm"
                            style={{
                              background: unitPreferences[activeSubCat.value] === 'inch'
                                ? 'linear-gradient(90deg, rgb(0, 208, 187) 0%, rgb(0, 126, 169) 100%)'
                                : 'transparent',
                              color: unitPreferences[activeSubCat.value] === 'inch' ? 'white' : '#6c757d',
                              borderColor: unitPreferences[activeSubCat.value] === 'inch' ? 'transparent' : '#6c757d',
                              transition: 'all 0.3s ease',
                              boxShadow: 'none'
                            }}
                            className="no-hover-toggle"
                          >
                            Inches
                          </ToggleButton>
                          <ToggleButton
                            id={`tbg-btn-1-${activeSubCat.value}`}
                            value="cm"
                            variant="outline-secondary"
                            size="sm"
                            style={{
                              background: unitPreferences[activeSubCat.value] === 'cm'
                                ? 'linear-gradient(90deg, rgb(0, 208, 187) 0%, rgb(0, 126, 169) 100%)'
                                : 'transparent',
                              color: unitPreferences[activeSubCat.value] === 'cm' ? 'white' : '#6c757d',
                              borderColor: unitPreferences[activeSubCat.value] === 'cm' ? 'transparent' : '#6c757d',
                              transition: 'all 0.3s ease',
                              boxShadow: 'none',
                            }}
                            className="no-hover-toggle"
                          >
                            CM
                          </ToggleButton>

                        </ToggleButtonGroup>
                      </div>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>

            {/* Subcategory Tabs */}
            {activeSubCategories.length > 0 && (
              <Row className="mb-3">
                <Col>
                  <div style={{ marginBottom: '20px' }}>
                    <Tabs
                      activeKey={activeSubCategoryTab}
                      onSelect={(k) => setActiveSubCategoryTab(Number(k))}
                      className="mb-3"
                    >
                      {activeSubCategories.map((subCat, subCatIndex) => (
                        <Tab
                          key={subCatIndex}
                          eventKey={subCatIndex}
                          title={
                            <span style={{
                              color: activeSubCategoryTab === subCatIndex ? 'black' : 'grey',
                              fontWeight: activeSubCategoryTab === subCatIndex ? 'bold' : 'normal',
                              whiteSpace: 'nowrap'
                            }}>
                              {subCat.label}
                            </span>
                          }
                        />
                      ))}
                    </Tabs>
                  </div>
                </Col>
              </Row>
            )}

            {/* Size Chart Table */}
            {activeSubCategories.length > 0 ? (
              <Row className="flex-grow-1">
                <Col>
                  {filteredSizes.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      No sizes available for the selected subcategory.
                    </div>
                  ) : filteredTypes.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      No size types available for the selected subcategory.
                    </div>
                  ) : (
                    <div style={{
                      width: '100%',
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem'
                    }}>
                      <Table striped bordered hover responsive style={{
                        width: '100%',
                        margin: 0,
                        tableLayout: 'fixed'
                      }}>
                        <thead>
                          <tr>
                            <th style={{
                              width: '150px',
                              minWidth: '150px',
                              textAlign: 'center',
                              backgroundColor: '#f8f9fa'
                            }}>
                              S/T
                            </th>
                            {filteredSizes.map((size, sizeIndex) => (
                              <th key={sizeIndex} style={{
                                width: '80px',
                                minWidth: '80px',
                                textAlign: 'center',
                                backgroundColor: '#f8f9fa'
                              }}>
                                {size.size}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTypes.map((type, typeIndex) => (
                            <tr key={typeIndex}>
                              <td style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                width: '80px',
                                minWidth: '80px',
                                textAlign: 'center',
                                backgroundColor: '#f8f9fa'
                              }}>{type.sizeType}</td>
                              {filteredSizes.map((size, sizeIndex) => (
                                <td key={sizeIndex} style={{
                                  width: '80px',
                                  minWidth: '80px',
                                  padding: '8px'
                                }}>
                                  <Form.Control
                                    type="text"
                                    value={
                                      inputValues[
                                      `${activeSubCat.categoryId}_${activeSubCat.value}_${size.size}_${type.sizeType}_${unitPreferences[activeSubCat.value]}`
                                      ] ?? (() => {
                                        const rawValue = sizeChartData?.[activeSubCat.categoryId]?.[activeSubCat.value]?.[size.size]?.[type.sizeType] || '';
                                        if (unitPreferences[activeSubCat.value] === 'inch' && rawValue !== '') {
                                          return cmToInch(rawValue);
                                        }
                                        return rawValue;
                                      })()
                                    }
                                    onChange={(e) => {
                                      const inputValue = e.target.value;

                                      if (!isValidNumberInput(inputValue)) {
                                        return;
                                      }
                                      const key = `${activeSubCat.categoryId}_${activeSubCat.value}_${size.size}_${type.sizeType}_${unitPreferences[activeSubCat.value]}`;

                                      setInputValues(prev => ({
                                        ...prev,
                                        [key]: inputValue
                                      }));

                                      let valueToStore = inputValue;
                                      if (unitPreferences[activeSubCat.value] === 'inch' && inputValue !== '') {
                                        valueToStore = inchToCm(inputValue);
                                      }
                                      handleSizeChartChange(
                                        activeSubCat.categoryId,
                                        activeSubCat.value,
                                        size.size,
                                        type.sizeType,
                                        valueToStore
                                      );
                                    }}
                                    placeholder=""
                                    style={{
                                      width: '100%',
                                      textAlign: 'center'
                                    }}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Col>
              </Row>
            ) : (
              <Row>
                <Col className="text-center text-muted">
                  No subcategories available for the selected category.
                </Col>
              </Row>
            )}
          </>
        )}

        {categories.length === 0 && (
          <Row>
            <Col className="text-center text-muted">
              Please select at least one category to configure the size chart.
            </Col>
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: '1px solid #dee2e6' }}>
        <div className="d-flex gap-2">
          {/* <Button className="cancel_btn" variant="secondary" onClick={handleClose}>
            Close
          </Button> */}
          <Button
            className="save_size_chart_btn"
            onClick={handleClose}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Next'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SizeChartModal;